import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import './FeatureManagement.css';

const ROLLOUT_MODES = [
  { key: 'GLOBAL',     label: 'Global',    hint: 'Tier check only (default)' },
  { key: 'INTERNAL',   label: 'Internal',  hint: 'Admins only' },
  { key: 'INDIVIDUAL', label: 'Allowlist', hint: 'Specific merchant IDs' },
  { key: 'BATCH',      label: 'Batch',     hint: 'Criteria: tier / country / signup window' },
];

const LAUNCH_BOOST_FEATURE_ID = 'marketing.launch_boost';
const DEFAULT_LAUNCH_BOOST_CONFIG = {
  senderMode: 'prefer_merchant',
  freeMerchantCap: 2000,
  tiers: {
    FREE: { perSend: 50, monthly: 50 },
    PRO: { perSend: 100, monthly: 500 },
    PREMIUM: { perSend: 200, monthly: 2000 },
  },
};

function launchBoostConfigFromFeature(feature) {
  return {
    ...DEFAULT_LAUNCH_BOOST_CONFIG,
    ...(feature?.rolloutCriteria?.launchBoost || {}),
    tiers: {
      ...DEFAULT_LAUNCH_BOOST_CONFIG.tiers,
      ...(feature?.rolloutCriteria?.launchBoost?.tiers || {}),
    },
  };
}

function toLimitNumber(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.floor(n));
}

const FeatureManagement = () => {
  const [features, setFeatures] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [featureStats, setFeatureStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  // Editing panel for allowlist / batch criteria — one feature open at a time.
  const [rolloutEditing, setRolloutEditing] = useState(null);
  // Merchant picker state (for INDIVIDUAL rollout mode).
  const [allowlistMerchants, setAllowlistMerchants] = useState([]); // hydrated chip data
  const [merchantSearchQuery, setMerchantSearchQuery] = useState('');
  const [merchantSearchResults, setMerchantSearchResults] = useState([]);
  const [merchantSearchLoading, setMerchantSearchLoading] = useState(false);
  // Structured criteria form state (for BATCH rollout mode).
  // `tier` uses tier identifiers dynamically loaded from /admin/tiers — do
  // NOT hardcode them; the admin can create/rename tiers at runtime.
  const [criteriaForm, setCriteriaForm] = useState({
    tier: [], country: '', signupBefore: '', signupAfter: '',
  });
  const [launchBoostSaving, setLaunchBoostSaving] = useState(false);
  const [newFeature, setNewFeature] = useState({
    featureId: '',
    name: '',
    description: '',
    category: 'PAYMENTS',
    enabledFor: ['PRO', 'PREMIUM'],
    core: false,
    isActive: true,
    sortOrder: 0
  });

  // Feature categories for better organization
  const featureCategories = {
    'ANALYTICS': 'Analytics',
    'STORE_MANAGEMENT': 'Store Management',
    'MARKETING': 'Marketing',
    'PAYMENTS': 'Payments',
    'INVENTORY': 'Inventory',
    'CUSTOMERS': 'Customers',
    'SHIPPING': 'Shipping',
    'REPORTS': 'Reports',
    'INTEGRATIONS': 'Integrations',
    'AI': 'AI Features'
  };

  // Load features and tiers on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [featuresResponse, tiersResponse, statsResponse] = await Promise.all([
        api.get('/admin/features'),
        api.get('/admin/tiers'),
        api.get('/admin/features/stats').catch(() => ({ stats: {} })),
      ]);

      setFeatures(featuresResponse.features || []);
      setTiers(tiersResponse.message || tiersResponse.tiers || []);
      setFeatureStats(statsResponse.stats || statsResponse.data?.stats || {});
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load features and tiers');
    } finally {
      setLoading(false);
    }
  };

  // Reload just the stats block (called after save / mode change so the
  // eligibility counts reflect the new config without a full refresh).
  const reloadStats = async () => {
    try {
      const res = await api.get('/admin/features/stats');
      setFeatureStats(res.stats || res.data?.stats || {});
    } catch (err) {
      // Silent — stats are advisory, not required for the page to work.
    }
  };

  // Change the rollout mode for a feature. Doesn't touch allowlist /
  // criteria — those live behind the "Edit access" panel.
  const handleRolloutModeChange = async (featureId, newMode) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const feature = features.find((f) => f.featureId === featureId);
      if (!feature) return;

      await api.put(`/admin/features/${featureId}`, { rolloutMode: newMode });

      setFeatures((prev) => prev.map((f) => (
        f.featureId === featureId ? { ...f, rolloutMode: newMode } : f
      )));
      setSuccess(`${feature.name} rollout mode set to ${newMode}`);
      reloadStats();
    } catch (err) {
      console.error('Error updating rollout mode:', err);
      setError(err.response?.data?.error || 'Failed to update rollout mode');
    } finally {
      setSaving(false);
    }
  };

  // Open the rollout access editor for a feature. Hydrates the merchant
  // chips (allowlist mode) and pre-populates the criteria form fields
  // (batch mode) so the admin doesn't have to hand-parse anything.
  const openRolloutEditor = async (feature) => {
    const mode = feature.rolloutMode || 'GLOBAL';
    const allowlist = Array.isArray(feature.rolloutAllowlist) ? feature.rolloutAllowlist : [];
    const criteria = feature.rolloutCriteria || {};

    setRolloutEditing({
      featureId: feature.featureId,
      featureName: feature.name,
      rolloutMode: mode,
      allowlistIds: allowlist,
    });
    setMerchantSearchQuery('');
    setMerchantSearchResults([]);
    setAllowlistMerchants([]);
    setCriteriaForm({
      tier: Array.isArray(criteria.tier) ? criteria.tier.map((t) => String(t).toUpperCase()) : [],
      country: Array.isArray(criteria.country) ? criteria.country.join(', ') : '',
      signupBefore: criteria.signupBefore ? String(criteria.signupBefore).slice(0, 10) : '',
      signupAfter: criteria.signupAfter ? String(criteria.signupAfter).slice(0, 10) : '',
    });

    // Hydrate the allowlist chips with real merchant data (email/store name)
    // so the admin isn't staring at bare UUIDs.
    if (mode === 'INDIVIDUAL' && allowlist.length) {
      try {
        const res = await api.get(`/admin/features/merchants/search?ids=${encodeURIComponent(allowlist.join(','))}`);
        setAllowlistMerchants(res.merchants || res.data?.merchants || []);
      } catch (err) {
        console.warn('Could not hydrate allowlist merchants:', err);
      }
    }
  };

  // Debounced merchant autocomplete for the allowlist picker.
  useEffect(() => {
    if (!rolloutEditing || rolloutEditing.rolloutMode !== 'INDIVIDUAL') return;
    const query = merchantSearchQuery.trim();
    if (query.length < 2) {
      setMerchantSearchResults([]);
      return;
    }
    let cancelled = false;
    setMerchantSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/admin/features/merchants/search?search=${encodeURIComponent(query)}&limit=10`);
        if (!cancelled) {
          setMerchantSearchResults(res.merchants || res.data?.merchants || []);
        }
      } catch {
        if (!cancelled) setMerchantSearchResults([]);
      } finally {
        if (!cancelled) setMerchantSearchLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [merchantSearchQuery, rolloutEditing]);

  const addMerchantToAllowlist = (merchant) => {
    if (!rolloutEditing) return;
    if (rolloutEditing.allowlistIds.includes(merchant.id)) return;
    setRolloutEditing({
      ...rolloutEditing,
      allowlistIds: [...rolloutEditing.allowlistIds, merchant.id],
    });
    setAllowlistMerchants((prev) => (
      prev.some((m) => m.id === merchant.id) ? prev : [...prev, merchant]
    ));
    setMerchantSearchQuery('');
    setMerchantSearchResults([]);
  };

  const removeMerchantFromAllowlist = (merchantId) => {
    if (!rolloutEditing) return;
    setRolloutEditing({
      ...rolloutEditing,
      allowlistIds: rolloutEditing.allowlistIds.filter((id) => id !== merchantId),
    });
    setAllowlistMerchants((prev) => prev.filter((m) => m.id !== merchantId));
  };

  // Save the allowlist / criteria for the currently-editing feature.
  const handleRolloutConfigSave = async () => {
    if (!rolloutEditing) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const { featureId, rolloutMode, allowlistIds } = rolloutEditing;

      const payload = {};

      if (rolloutMode === 'INDIVIDUAL') {
        payload.rolloutAllowlist = allowlistIds;
      }

      if (rolloutMode === 'BATCH') {
        // Build the criteria object from the structured form. Only emit
        // keys that were actually filled in — an empty form clears the
        // criteria back to null so the feature has no gating within the
        // batch mode (admin should use INTERNAL for closed rollouts).
        const criteria = {};
        if (criteriaForm.tier.length > 0) {
          criteria.tier = criteriaForm.tier;
        }
        const countries = criteriaForm.country
          .split(/[\s,]+/)
          .map((c) => c.trim().toUpperCase())
          .filter(Boolean);
        if (countries.length > 0) {
          criteria.country = countries;
        }
        if (criteriaForm.signupBefore) criteria.signupBefore = criteriaForm.signupBefore;
        if (criteriaForm.signupAfter) criteria.signupAfter = criteriaForm.signupAfter;

        payload.rolloutCriteria = Object.keys(criteria).length ? criteria : null;
      }

      const res = await api.put(`/admin/features/${featureId}`, payload);
      const updated = res?.data?.feature;

      setFeatures((prev) => prev.map((f) => (
        f.featureId === featureId
          ? { ...f, ...(updated || {}) }
          : f
      )));
      setSuccess('Rollout access updated');
      setRolloutEditing(null);
      reloadStats();
    } catch (err) {
      console.error('Error saving rollout config:', err);
      setError(err.response?.data?.error || 'Failed to save rollout access');
    } finally {
      setSaving(false);
    }
  };

  // Handle tier toggle for a feature
  const handleTierToggle = async (featureId, tier, isEnabled) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const feature = features.find(f => f.featureId === featureId);
      if (!feature) return;

      const newEnabledFor = isEnabled 
        ? [...feature.enabledFor, tier]
        : feature.enabledFor.filter(t => t !== tier);

      await api.put(`/admin/features/${featureId}`, {
        enabledFor: newEnabledFor
      });

      // Update local state
      setFeatures(prev => prev.map(f => 
        f.featureId === featureId 
          ? { ...f, enabledFor: newEnabledFor }
          : f
      ));

      setSuccess(`Feature ${feature.name} updated successfully`);
    } catch (error) {
      console.error('Error updating feature:', error);
      setError('Failed to update feature');
    } finally {
      setSaving(false);
    }
  };

  const handleLaunchBoostConfigChange = async (feature, updater) => {
    if (!feature || feature.featureId !== LAUNCH_BOOST_FEATURE_ID) return;
    try {
      setLaunchBoostSaving(true);
      setError('');
      setSuccess('');

      const currentConfig = launchBoostConfigFromFeature(feature);
      const nextConfig = updater(currentConfig);
      const rolloutCriteria = {
        ...(feature.rolloutCriteria || {}),
        launchBoost: nextConfig,
      };

      const res = await api.put(`/admin/features/${feature.featureId}`, { rolloutCriteria });
      const updated = res?.data?.feature;

      setFeatures((prev) => prev.map((f) => (
        f.featureId === feature.featureId
          ? { ...f, ...(updated || {}), rolloutCriteria }
          : f
      )));
      setSuccess('Launch Boost controls updated');
      reloadStats();
    } catch (error) {
      console.error('Error updating Launch Boost controls:', error);
      setError(error.response?.data?.error || 'Failed to update Launch Boost controls');
    } finally {
      setLaunchBoostSaving(false);
    }
  };

  // Seed default features and tiers
  const handleSeed = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // First seed tiers
      try {
        await api.post('/admin/tiers/seed');
      } catch (tierError) {
        console.log('Tiers may already exist:', tierError);
      }

      // Then seed features
      await api.post('/admin/features/seed');

      await loadData();
      setSuccess('Default features and tiers seeded successfully');
    } catch (error) {
      console.error('Error seeding data:', error);
      setError(error.message || 'Failed to seed default data');
    } finally {
      setSaving(false);
    }
  };

  // Create a new feature
  const handleCreateFeature = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      if (!newFeature.featureId || !newFeature.name) {
        setError('Feature ID and Name are required');
        setSaving(false);
        return;
      }

      await api.post('/admin/features', newFeature);
      await loadData();
      setSuccess(`Feature "${newFeature.name}" created successfully`);
      setShowCreateModal(false);
      setNewFeature({
        featureId: '',
        name: '',
        description: '',
        category: 'PAYMENTS',
        enabledFor: ['PRO', 'PREMIUM'],
        core: false,
        isActive: true,
        sortOrder: 0
      });
    } catch (error) {
      console.error('Error creating feature:', error);
      setError(error.message || 'Failed to create feature');
    } finally {
      setSaving(false);
    }
  };

  // Add missing features (Tags & Segments)
  const handleEnsureFeatures = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await api.post('/admin/features/ensure');

      await loadData();
      const results = response.data?.data || [];
      const created = results.filter(r => r.status === 'created').length;
      const existing = results.filter(r => r.status === 'already exists').length;
      setSuccess(`Features updated: ${created} created, ${existing} already existed`);
    } catch (error) {
      console.error('Error ensuring features:', error);
      setError(error.message || 'Failed to add missing features');
    } finally {
      setSaving(false);
    }
  };

  // Group features by category
  const groupedFeatures = features.reduce((acc, feature) => {
    const category = feature.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(feature);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="feature-management">
        <div className="loading">Loading features and tiers...</div>
      </div>
    );
  }

  return (
    <div className="feature-management">
      <div className="feature-management-header">
        <h1>Feature Management</h1>
        <p>Manage which features are available for each subscription tier</p>
        
        <div className="feature-management-actions">
          <button
            className="btn btn-primary"
            onClick={handleSeed}
            disabled={saving}
          >
            {saving ? 'Seeding...' : 'Seed Default Features'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleEnsureFeatures}
            disabled={saving}
            style={{ marginLeft: '10px' }}
          >
            {saving ? 'Adding...' : 'Add Missing Features'}
          </button>
          <button
            className="btn btn-success"
            onClick={() => setShowCreateModal(true)}
            disabled={saving}
            style={{ marginLeft: '10px', backgroundColor: '#10b981', color: 'white' }}
          >
            + Create Feature
          </button>
        </div>
      </div>

      {/* Create Feature Modal */}
      {showCreateModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '500px',
            maxWidth: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '600' }}>Create New Feature</h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Feature ID *</label>
              <input
                type="text"
                placeholder="e.g., sales.pos"
                value={newFeature.featureId}
                onChange={(e) => setNewFeature({ ...newFeature, featureId: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Name *</label>
              <input
                type="text"
                placeholder="e.g., Point of Sale"
                value={newFeature.name}
                onChange={(e) => setNewFeature({ ...newFeature, name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Description</label>
              <textarea
                placeholder="Feature description..."
                value={newFeature.description}
                onChange={(e) => setNewFeature({ ...newFeature, description: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Category</label>
              <select
                value={newFeature.category}
                onChange={(e) => setNewFeature({ ...newFeature, category: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              >
                {Object.entries(featureCategories).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Enabled For Tiers</label>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {tiers.map(tier => (
                  <label key={tier.tier} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newFeature.enabledFor.includes(tier.tier)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewFeature({ ...newFeature, enabledFor: [...newFeature.enabledFor, tier.tier] });
                        } else {
                          setNewFeature({ ...newFeature, enabledFor: newFeature.enabledFor.filter(t => t !== tier.tier) });
                        }
                      }}
                    />
                    <span style={{ fontSize: '14px' }}>{tier.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFeature}
                disabled={saving}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {saving ? 'Creating...' : 'Create Feature'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      <div className="feature-management-content">
        <div className="tiers-info">
          <h2>Subscription Tiers</h2>
          <div className="tiers-grid">
            {tiers.map(tier => {
              // Count how many features are enabled for this tier
              const enabledFeaturesCount = features.filter(feature =>
                feature.enabledFor && feature.enabledFor.includes(tier.tier)
              ).length;

              // Default to NGN currency symbol
              const currencySymbol = '₦';

              return (
                <div key={tier.tier} className="tier-card">
                  <h3>{tier.name}</h3>
                  <div className="tier-price">
                    {tier.monthlyPrice === 0 ? 'Free' : `${currencySymbol}${tier.monthlyPrice.toLocaleString()}/month`}
                  </div>
                  <p>{tier.description}</p>
                  <div className="tier-features-count">
                    {enabledFeaturesCount} feature{enabledFeaturesCount !== 1 ? 's' : ''} enabled
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="features-management">
          <h2>Feature Access by Tier</h2>
          <p>Check the boxes to enable features for each tier</p>
          
          {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => (
            <div key={category} className="feature-category">
              <h3>{featureCategories[category] || category}</h3>
              <div className="features-table">
                <div className="features-table-header">
                  <div className="feature-name">Feature</div>
                  <div className="tier-columns">
                    {tiers.map(tier => (
                      <div key={tier.tier} className="tier-column">
                        {tier.name}
                      </div>
                    ))}
                  </div>
                </div>
                
                {categoryFeatures.map(feature => {
                  const mode = feature.rolloutMode || 'GLOBAL';
                  const allowlistCount = Array.isArray(feature.rolloutAllowlist) ? feature.rolloutAllowlist.length : 0;
                  const hasCriteria = feature.rolloutCriteria && Object.keys(feature.rolloutCriteria).length > 0;
                  const needsConfig = mode === 'INDIVIDUAL' || mode === 'BATCH';
                  const stats = featureStats[feature.featureId] || {};
                  const fmt = (n) => (n == null ? '—' : n.toLocaleString());
                  return (
                    <div key={feature.featureId} className="feature-row">
                      <div className="feature-info">
                        <div className="feature-name">
                          {feature.name}
                          {mode !== 'GLOBAL' && (
                            <span className={`fm-rollout-badge fm-rollout-badge--${mode.toLowerCase()}`}>
                              {mode === 'INTERNAL' && 'Internal'}
                              {mode === 'INDIVIDUAL' && `Allowlist · ${allowlistCount}`}
                              {mode === 'BATCH' && (hasCriteria ? 'Batch' : 'Batch (no criteria)')}
                            </span>
                          )}
                        </div>
                        <div className="feature-description">{feature.description}</div>
                        {feature.core && (
                          <span className="core-badge">Core Feature</span>
                        )}
                        <div className="fm-stats-row" title="Merchants with access · actively enabled · used in last 30 days">
                          <span className="fm-stat">
                            <span className="fm-stat__label">Eligible</span>
                            <span className="fm-stat__value">{fmt(stats.eligible)}</span>
                          </span>
                          <span className="fm-stat" title="Merchants who have actively opted into the feature (feature-specific measurement)">
                            <span className="fm-stat__label">Enabled</span>
                            <span className="fm-stat__value">{fmt(stats.enabled)}</span>
                          </span>
                          <span className="fm-stat" title="Distinct merchants who used the feature in the last 30 days">
                            <span className="fm-stat__label">30d Active</span>
                            <span className="fm-stat__value">{fmt(stats.active30d)}</span>
                          </span>
                        </div>
                        <div className="fm-rollout-row">
                          <label className="fm-rollout-label">Rollout</label>
                          <select
                            className="fm-rollout-select"
                            value={mode}
                            disabled={saving}
                            onChange={(e) => handleRolloutModeChange(feature.featureId, e.target.value)}
                          >
                            {ROLLOUT_MODES.map((m) => (
                              <option key={m.key} value={m.key} title={m.hint}>{m.label}</option>
                            ))}
                          </select>
                          {needsConfig && (
                            <button
                              type="button"
                              className="fm-rollout-edit"
                              onClick={() => openRolloutEditor(feature)}
                            >
                              {mode === 'INDIVIDUAL' ? `Edit allowlist (${allowlistCount})` : 'Edit criteria'}
                            </button>
                          )}
                        </div>
                        {feature.featureId === LAUNCH_BOOST_FEATURE_ID && (
                          <div className="fm-launch-boost-controls">
                            <div className="fm-launch-boost-head">
                              <strong>Launch Boost controls</strong>
                              <span>Free cohort, sender mode, and usage caps</span>
                            </div>
                            <div className="fm-launch-boost-grid">
                              <label className="fm-launch-boost-field">
                                <span>Sender</span>
                                <select
                                  value={launchBoostConfigFromFeature(feature).senderMode}
                                  disabled={saving || launchBoostSaving}
                                  onChange={(e) => handleLaunchBoostConfigChange(feature, (config) => ({
                                    ...config,
                                    senderMode: e.target.value,
                                  }))}
                                >
                                  <option value="prefer_merchant">Merchant, fallback platform</option>
                                  <option value="platform">Platform only</option>
                                  <option value="merchant">Merchant only</option>
                                </select>
                              </label>
                              <label className="fm-launch-boost-field">
                                <span>Free merchant cap</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={launchBoostConfigFromFeature(feature).freeMerchantCap}
                                  disabled={saving || launchBoostSaving}
                                  onChange={(e) => handleLaunchBoostConfigChange(feature, (config) => ({
                                    ...config,
                                    freeMerchantCap: toLimitNumber(e.target.value, DEFAULT_LAUNCH_BOOST_CONFIG.freeMerchantCap),
                                  }))}
                                />
                              </label>
                            </div>
                            <div className="fm-launch-boost-tiers">
                              {tiers.map((tier) => {
                                const tierKey = String(tier.tier).toUpperCase();
                                const config = launchBoostConfigFromFeature(feature);
                                const limits = config.tiers[tierKey] || { perSend: 50, monthly: 50 };
                                return (
                                  <div key={tierKey} className="fm-launch-boost-tier">
                                    <span className="fm-launch-boost-tier__name">{tier.name || tier.tier}</span>
                                    <label>
                                      <span>Per send</span>
                                      <input
                                        type="number"
                                        min="0"
                                        value={limits.perSend}
                                        disabled={saving || launchBoostSaving}
                                        onChange={(e) => handleLaunchBoostConfigChange(feature, (current) => ({
                                          ...current,
                                          tiers: {
                                            ...current.tiers,
                                            [tierKey]: {
                                              ...(current.tiers[tierKey] || {}),
                                              perSend: toLimitNumber(e.target.value, limits.perSend),
                                            },
                                          },
                                        }))}
                                      />
                                    </label>
                                    <label>
                                      <span>Monthly</span>
                                      <input
                                        type="number"
                                        min="0"
                                        value={limits.monthly}
                                        disabled={saving || launchBoostSaving}
                                        onChange={(e) => handleLaunchBoostConfigChange(feature, (current) => ({
                                          ...current,
                                          tiers: {
                                            ...current.tiers,
                                            [tierKey]: {
                                              ...(current.tiers[tierKey] || {}),
                                              monthly: toLimitNumber(e.target.value, limits.monthly),
                                            },
                                          },
                                        }))}
                                      />
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="tier-toggles">
                        {tiers.map(tier => (
                          <div key={tier.tier} className="tier-toggle">
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={feature.enabledFor.includes(tier.tier)}
                                onChange={(e) => handleTierToggle(feature.featureId, tier.tier, e.target.checked)}
                                disabled={saving || mode !== 'GLOBAL'}
                                title={mode !== 'GLOBAL' ? 'Tier gating is bypassed when rollout mode is not Global' : ''}
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Rollout access editor — allowlist for INDIVIDUAL, criteria JSON for BATCH */}
      {rolloutEditing && (
        <div className="fm-rollout-modal-overlay" onClick={() => !saving && setRolloutEditing(null)}>
          <div className="fm-rollout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fm-rollout-modal-head">
              <div>
                <h3>Rollout access</h3>
                <p>{rolloutEditing.featureName} — mode: {rolloutEditing.rolloutMode}</p>
              </div>
              <button type="button" className="fm-rollout-close" onClick={() => setRolloutEditing(null)} disabled={saving}>
                ×
              </button>
            </div>

            {rolloutEditing.rolloutMode === 'INDIVIDUAL' && (
              <div className="fm-rollout-body">
                <div className="fm-picker-label">
                  <strong>Merchants with access</strong>
                  <span className="fm-picker-count">{rolloutEditing.allowlistIds.length}</span>
                </div>
                <p className="fm-rollout-help">
                  Only these merchants can access this feature — tier is
                  bypassed. Search by email, store name, or merchant code.
                </p>

                <div className="fm-chips">
                  {rolloutEditing.allowlistIds.length === 0 && (
                    <span className="fm-chips-empty">No merchants added yet</span>
                  )}
                  {rolloutEditing.allowlistIds.map((id) => {
                    const m = allowlistMerchants.find((x) => x.id === id);
                    const label = m ? (m.storeName || m.email) : id.slice(0, 8);
                    const sub = m ? (m.email !== label ? m.email : m.merchantTier) : null;
                    return (
                      <span key={id} className="fm-chip">
                        <span className="fm-chip__main">
                          <span className="fm-chip__label">{label}</span>
                          {sub && <span className="fm-chip__sub">{sub}</span>}
                        </span>
                        <button
                          type="button"
                          className="fm-chip__remove"
                          onClick={() => removeMerchantFromAllowlist(id)}
                          disabled={saving}
                          aria-label={`Remove ${label}`}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>

                <div className="fm-search">
                  <input
                    type="text"
                    className="fm-search__input"
                    value={merchantSearchQuery}
                    onChange={(e) => setMerchantSearchQuery(e.target.value)}
                    placeholder="Search merchants…"
                    disabled={saving}
                  />
                  {merchantSearchLoading && <span className="fm-search__loading">…</span>}
                  {merchantSearchResults.length > 0 && (
                    <div className="fm-search__results">
                      {merchantSearchResults.map((m) => {
                        const already = rolloutEditing.allowlistIds.includes(m.id);
                        return (
                          <button
                            type="button"
                            key={m.id}
                            className="fm-search__result"
                            onClick={() => addMerchantToAllowlist(m)}
                            disabled={saving || already}
                          >
                            <span className="fm-search__result-main">
                              <span className="fm-search__result-label">{m.storeName || m.email}</span>
                              <span className="fm-search__result-tier">{m.merchantTier}</span>
                            </span>
                            <span className="fm-search__result-sub">
                              {m.email}{m.merchantCode ? ` · ${m.merchantCode}` : ''}
                            </span>
                            {already && <span className="fm-search__result-added">Added</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {merchantSearchQuery.trim().length >= 2 && !merchantSearchLoading && merchantSearchResults.length === 0 && (
                    <div className="fm-search__empty">No matches</div>
                  )}
                </div>
              </div>
            )}

            {rolloutEditing.rolloutMode === 'BATCH' && (
              <div className="fm-rollout-body">
                <p className="fm-rollout-help">
                  Merchants matching <strong>all</strong> present criteria get
                  access. Empty fields are ignored.
                </p>

                <div className="fm-criteria-group">
                  <label className="fm-criteria-label">Tier</label>
                  <div className="fm-criteria-tiers">
                    {tiers.length === 0 && (
                      <span className="fm-criteria-empty">
                        No tiers configured. Set them up in the tiers section
                        before targeting by tier.
                      </span>
                    )}
                    {tiers.map((tier) => {
                      const key = String(tier.tier).toUpperCase();
                      const checked = criteriaForm.tier.includes(key);
                      return (
                        <label key={key} className="fm-criteria-tier">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...criteriaForm.tier, key]
                                : criteriaForm.tier.filter((t) => t !== key);
                              setCriteriaForm({ ...criteriaForm, tier: next });
                            }}
                            disabled={saving}
                          />
                          <span>{tier.name || tier.tier}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="fm-criteria-group">
                  <label className="fm-criteria-label" htmlFor="fm-crit-country">Country</label>
                  <input
                    id="fm-crit-country"
                    type="text"
                    className="fm-criteria-input"
                    value={criteriaForm.country}
                    onChange={(e) => setCriteriaForm({ ...criteriaForm, country: e.target.value })}
                    placeholder="NG, GH, KE (comma-separated ISO codes)"
                    disabled={saving}
                  />
                </div>

                <div className="fm-criteria-row">
                  <div className="fm-criteria-group">
                    <label className="fm-criteria-label" htmlFor="fm-crit-after">Signup on or after</label>
                    <input
                      id="fm-crit-after"
                      type="date"
                      className="fm-criteria-input"
                      value={criteriaForm.signupAfter}
                      onChange={(e) => setCriteriaForm({ ...criteriaForm, signupAfter: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                  <div className="fm-criteria-group">
                    <label className="fm-criteria-label" htmlFor="fm-crit-before">Signup before</label>
                    <input
                      id="fm-crit-before"
                      type="date"
                      className="fm-criteria-input"
                      value={criteriaForm.signupBefore}
                      onChange={(e) => setCriteriaForm({ ...criteriaForm, signupBefore: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="fm-rollout-actions">
              <button type="button" className="fm-rollout-btn fm-rollout-btn--ghost" onClick={() => setRolloutEditing(null)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="fm-rollout-btn fm-rollout-btn--primary" onClick={handleRolloutConfigSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureManagement;
